import React, { useState, useEffect } from 'react';

interface Photo {
  id: number;
  url: string;
  event_name: string;
  photographer: {
    name: string;
  };
}

interface PhotoCustomizationProps {
  photoData: Photo;
  cartId: number;
  onAddToCart: (photoId: number, customizations: any) => void;
  onRemoveFromCart: (photoId: number) => void;
}

interface Customization {
  size: string;
  quantity: number;
  printType: string;
}

const PhotoCustomization: React.FC<PhotoCustomizationProps> = ({
  photoData,
  cartId,
  onAddToCart,
  onRemoveFromCart
}) => {
  const [customizations, setCustomizations] = useState<Customization>({
    size: '5x7',
    quantity: 1,
    printType: 'standard'
  });

  const [isInCart, setIsInCart] = useState(false);
  const [loading, setLoading] = useState(false);

  const printSizes = [
    { value: '4x6', label: '4x6 inches', price: 5.99 },
    { value: '5x7', label: '5x7 inches', price: 7.99 },
    { value: '8x10', label: '8x10 inches', price: 12.99 },
    { value: '11x14', label: '11x14 inches', price: 19.99 }
  ];

  const printTypes = [
    { value: 'standard', label: 'Standard Print', priceMultiplier: 1 },
    { value: 'premium', label: 'Premium Print', priceMultiplier: 1.5 },
    { value: 'canvas', label: 'Canvas Print', priceMultiplier: 2 }
  ];

  const selectedSizeData = printSizes.find(size => size.value === customizations.size);
  const selectedTypeData = printTypes.find(type => type.value === customizations.printType);
  
  const totalPrice = selectedSizeData && selectedTypeData 
    ? selectedSizeData.price * selectedTypeData.priceMultiplier * customizations.quantity
    : 0;

  const handleCustomizationChange = (field: keyof Customization, value: string | number) => {
    setCustomizations(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleAddToCart = async () => {
    setLoading(true);
    try {
      await onAddToCart(photoData.id, {
        ...customizations,
        totalPrice
      });
      setIsInCart(true);
    } catch (error) {
      console.error('Failed to add to cart:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveFromCart = async () => {
    setLoading(true);
    try {
      await onRemoveFromCart(photoData.id);
      setIsInCart(false);
    } catch (error) {
      console.error('Failed to remove from cart:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="photo-customization-container">
      <div className="row">
        <div className="col-md-6">
          <div className="photo-preview">
            <img 
              src={photoData.url} 
              alt="Photo preview" 
              className="img-responsive"
              style={{ maxWidth: '100%', height: 'auto' }}
            />
            <div className="photo-details mt-3">
              <h5>{photoData.event_name}</h5>
              <p className="text-muted">by {photoData.photographer.name}</p>
            </div>
          </div>
        </div>
        
        <div className="col-md-6">
          <div className="customization-options">
            <h4>Customize Your Print</h4>
            
            <div className="form-group">
              <label htmlFor="size-select">Print Size:</label>
              <select 
                id="size-select"
                className="form-control"
                value={customizations.size}
                onChange={(e) => handleCustomizationChange('size', e.target.value)}
              >
                {printSizes.map(size => (
                  <option key={size.value} value={size.value}>
                    {size.label} - ${size.price.toFixed(2)}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="type-select">Print Type:</label>
              <select 
                id="type-select"
                className="form-control"
                value={customizations.printType}
                onChange={(e) => handleCustomizationChange('printType', e.target.value)}
              >
                {printTypes.map(type => (
                  <option key={type.value} value={type.value}>
                    {type.label} {type.priceMultiplier > 1 && `(+${((type.priceMultiplier - 1) * 100).toFixed(0)}%)`}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="quantity-input">Quantity:</label>
              <input 
                id="quantity-input"
                type="number"
                className="form-control"
                min="1"
                max="10"
                value={customizations.quantity}
                onChange={(e) => handleCustomizationChange('quantity', parseInt(e.target.value) || 1)}
              />
            </div>

            <div className="price-summary mb-3">
              <h5>Total: ${totalPrice.toFixed(2)}</h5>
            </div>

            <div className="pc-action-buttons">
              {isInCart ? (
                <button 
                  className="btn btn-danger btn-block"
                  onClick={handleRemoveFromCart}
                  disabled={loading}
                >
                  {loading ? 'Removing...' : 'Remove from Cart'}
                </button>
              ) : (
                <button 
                  className="btn btn-primary btn-block"
                  onClick={handleAddToCart}
                  disabled={loading}
                >
                  {loading ? 'Adding...' : 'Add to Cart'}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PhotoCustomization;